/**
 * The footer: the site's map, who publishes it, and the one paragraph that is a
 * condition rather than a feature.
 *
 * The provider-terms sentence is carried in full and near the bottom on purpose.
 * Kimene manages an operator's own trusted profiles; it is not a
 * credential-sharing service and not a way around a provider's limits. A page
 * that left that out would be selling a different product from the one that
 * ships.
 *
 * The company block is here as well as on two other pages, and the repetition is
 * deliberate rather than an oversight: a footer is where a reader looks for a
 * postal address without wanting to navigate, and every one of these values is
 * read from `data/links.ts`, so there is one place to change a telephone number
 * however many times it is printed.
 */
import { useLocale, useT } from '../i18n/context'
import { COMPANY } from '../data/links'
import { NAV_PAGES, pathFor, type PageId } from '../routes'
import type { MessageKey } from '../i18n'

const NAV_LABEL: Record<PageId, MessageKey> = {
  home: 'nav.home',
  product: 'nav.product',
  security: 'nav.security',
  company: 'nav.company',
  contact: 'nav.contact',
}

export function SiteFooter() {
  const t = useT()
  const locale = useLocale()
  const year = 2026

  return (
    <footer className="kl-footer">
      <div className="kl-inner kl-footer__grid">
        <div className="kl-footer__col">
          <p className="kl-footer__heading">{t('foot.siteHeading')}</p>
          <ul className="kl-footer__links">
            <li>
              <a href={pathFor(locale, 'home')}>{t('nav.home')}</a>
            </li>
            {NAV_PAGES.map((id) => (
              <li key={id}>
                <a href={pathFor(locale, id)}>{t(NAV_LABEL[id])}</a>
              </li>
            ))}
          </ul>
        </div>

        <div className="kl-footer__col">
          <p className="kl-footer__heading">{t('foot.contactHeading')}</p>
          <address className="kl-footer__address">
            <span className="kl-footer__legal">{COMPANY.legalName}</span>
            <span>{COMPANY.address}</span>
            <span>
              {COMPANY.district} · {COMPANY.country}
            </span>
            <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
            <a className="k-mono" href={COMPANY.phoneHref}>
              {COMPANY.phone}
            </a>
          </address>
        </div>
      </div>

      <div className="kl-inner kl-footer__fine">
        <p className="kl-footer__terms">{t('foot.providerTerms')}</p>
        <p>{t('foot.builtWith')}</p>
        <p>
          © {year} {COMPANY.legalName}. {t('foot.rights')}
        </p>
      </div>
    </footer>
  )
}
